import type { ReactElement, ChangeEvent, KeyboardEvent } from 'react';
import type { StoryVersion } from '@/types/story';

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import VersionBadge from '@/components/VersionBadge';
import { User, Edit2, Camera, BookOpen, Heart, Sparkles, X, Check, Loader2, Gift, Shield, Crown, Coins, ChevronRight, ChevronLeft, Receipt, RotateCcw, AlertTriangle, Users, Trash2, Eye, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { getBookTypeLabel } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Interfaces
interface UserProfile {
  id: string;
  name: string;
  email: string;
  handle?: string;
  bio?: string;
  picture?: string;
  credits: number;
  story_count?: number;
  series_count?: number;
  favorites?: string[];
  followers_count?: number;
  following_count?: number;
  is_premium?: boolean;
  is_admin?: boolean;
  subscription_plan?: string;
}

interface ProfileStory {
  id: string;
  title: string;
  thumbnail?: string;
  prompt?: string;
  category: string;
  age_range?: string;
  created_at: string;
  version?: StoryVersion;
  is_pro_story?: boolean;
  is_public?: boolean;
  views_count?: number;
  favorites_count?: number;
  pages?: { page_number: number }[];
}

interface Subscription {
  plan?: string;
  plan_name?: string;
  cancel_at_period_end?: boolean;
  current_period_end?: string;
  credits_per_month?: number;
}

interface Transaction {
  transaction_id?: string;
  type: string;
  package?: string;
  plan?: string;
  old_plan?: string;
  credits_added?: number;
  amount_formatted: string;
  created_at: string;
}

interface BillingHistoryResponse {
  transactions: Transaction[];
  total: number;
}

interface PlanBenefit {
  name: string;
  price: string;
  benefits: string[];
}

// Helper to get full image URL (handles both local /static/ paths and external URLs)
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Plan benefits for display - must match backend/services/stripe_service.py
const PLAN_BENEFITS: Record<string, PlanBenefit> = {
  lite: {
    name: 'Lite',
    price: '$5.99/month',
    benefits: ['2 credits per month', '10% off prints', 'Audio narration', 'Photo transform']
  },
  standard: {
    name: 'Standard',
    price: '$11.99/month',
    benefits: ['5 credits per month', '15% off prints', 'Audio narration', 'Photo transform']
  },
  family: {
    name: 'Family',
    price: '$19.99/month',
    benefits: ['10 credits per month', '20% off prints', 'Audio narration', 'Photo transform']
  },
  creator: {
    name: 'Creator',
    price: '$39.99/month',
    benefits: ['20 credits per month', '25% off prints', 'Series feature', 'Priority generation']
  },
  pro_creator: {
    name: 'Pro Creator',
    price: '$59.99/month',
    benefits: ['35 credits per month', '30% off prints', 'Series feature', 'Priority generation']
  }
};

const ProfilePage = (): ReactElement => {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserProfile | null>(() => {
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [stories, setStories] = useState<ProfileStory[]>([]);
  const [loadingStories, setLoadingStories] = useState<boolean>(false);
  const [totalStories, setTotalStories] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const STORIES_PER_PAGE = 10;
  const [uploadingAvatar, setUploadingAvatar] = useState<boolean>(false);
  const [promoCode, setPromoCode] = useState<string>('');
  const [redeemLoading, setRedeemLoading] = useState<boolean>(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [cancelingSubscription, setCancelingSubscription] = useState<boolean>(false);
  const [reactivatingSubscription, setReactivatingSubscription] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [billingHistory, setBillingHistory] = useState<Transaction[]>([]);
  const [showBillingHistory, setShowBillingHistory] = useState<boolean>(false);
  const [loadingBillingHistory, setLoadingBillingHistory] = useState<boolean>(false);
  const [billingHistoryTotal, setBillingHistoryTotal] = useState<number>(0);
  const [billingHistoryPage, setBillingHistoryPage] = useState<number>(0);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState<boolean>(false);
  const BILLING_PAGE_SIZE = 10;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState<string>('');
  const [deletingAccount, setDeletingAccount] = useState<boolean>(false);
  const [showDangerZone, setShowDangerZone] = useState<boolean>(false);

  // Edit form state
  const [editName, setEditName] = useState<string>('');
  const [editHandle, setEditHandle] = useState<string>('');
  const [editBio, setEditBio] = useState<string>('');
  const [editPicture, setEditPicture] = useState<string>('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async (): Promise<void> => {
    try {
      const response = await axios.get<UserProfile>(`${API}/profile`, {
        withCredentials: true
      });
      setUser(response.data);
      setEditName(response.data.name || '');
      setEditHandle(response.data.handle || '');
      setEditBio(response.data.bio || '');
      setEditPicture(response.data.picture || '');

      // Load user's stories with pagination
      await loadStories(1);

      // Load subscription data if user is premium
      if (response.data.is_premium) {
        try {
          const subRes = await axios.get<Subscription>(`${API}/v1/payments/subscription`, {
            withCredentials: true
          });
          setSubscription(subRes.data);
        } catch (subError) {
          console.error('Failed to load subscription:', subError);
        }
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStories = async (page: number = 1): Promise<void> => {
    setLoadingStories(true);

    try {
      const offset = (page - 1) * STORIES_PER_PAGE;
      const params = new URLSearchParams();
      params.append('limit', STORIES_PER_PAGE.toString());
      params.append('offset', offset.toString());
      params.append('sort', 'newest');

      interface LibraryResponse {
        stories: ProfileStory[];
        total: number;
        hasMore: boolean;
      }

      const storiesRes = await axios.get<LibraryResponse>(`${API}/v1/stories/library?${params}`, {
        withCredentials: true
      });

      setStories(storiesRes.data.stories);
      setTotalStories(storiesRes.data.total);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to load stories:', error);
    } finally {
      setLoadingStories(false);
    }
  };

  const totalPages = Math.ceil(totalStories / STORIES_PER_PAGE);

  const handlePageChange = (page: number): void => {
    if (page >= 1 && page <= totalPages) {
      loadStories(page);
      // Scroll to stories section
      document.getElementById('my-stories-section')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCancelSubscription = (): void => {
    setShowCancelModal(true);
  };

  const confirmCancelSubscription = async (): Promise<void> => {
    setShowCancelModal(false);
    setCancelingSubscription(true);
    try {
      await axios.post(`${API}/v1/payments/subscription/cancel`, {}, { withCredentials: true });
      toast.success('Subscription will be canceled at the end of your billing period.');
      // Refresh subscription data
      try {
        const subRes = await axios.get<Subscription>(`${API}/v1/payments/subscription`, { withCredentials: true });
        setSubscription(subRes.data);
      } catch (subError) {
        // If we can't fetch subscription status, just update local state
        console.warn('Could not refresh subscription status:', subError);
        setSubscription(prev => prev ? { ...prev, cancel_at_period_end: true } : null);
      }
      // Also refresh user profile
      loadProfile();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
      } else {
        toast.error('Failed to cancel subscription');
      }
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleReactivateSubscription = async (): Promise<void> => {
    setReactivatingSubscription(true);
    try {
      await axios.post(`${API}/v1/payments/subscription/reactivate`, {}, { withCredentials: true });
      toast.success('Subscription reactivated! You will continue to be billed.');
      // Refresh subscription data
      try {
        const subRes = await axios.get<Subscription>(`${API}/v1/payments/subscription`, { withCredentials: true });
        setSubscription(subRes.data);
      } catch (subError) {
        console.warn('Could not refresh subscription status:', subError);
        setSubscription(prev => prev ? { ...prev, cancel_at_period_end: false } : null);
      }
      loadProfile();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
      } else {
        toast.error('Failed to reactivate subscription');
      }
    } finally {
      setReactivatingSubscription(false);
    }
  };

  const loadBillingHistory = async (loadMore: boolean = false): Promise<void> => {
    if (!loadMore && billingHistory.length > 0) return; // Already loaded initial page

    if (loadMore) {
      setLoadingMoreHistory(true);
    } else {
      setLoadingBillingHistory(true);
    }

    try {
      const offset = loadMore ? billingHistory.length : 0;
      const res = await axios.get<BillingHistoryResponse>(
        `${API}/v1/payments/history?limit=${BILLING_PAGE_SIZE}&offset=${offset}`,
        { withCredentials: true }
      );

      const newTransactions = res.data.transactions || [];
      const total = res.data.total || 0;

      if (loadMore) {
        setBillingHistory(prev => [...prev, ...newTransactions]);
      } else {
        setBillingHistory(newTransactions);
      }
      setBillingHistoryTotal(total);
      setBillingHistoryPage(prev => prev + 1);
    } catch (error) {
      console.error('Failed to load billing history:', error);
      toast.error('Failed to load billing history');
    } finally {
      setLoadingBillingHistory(false);
      setLoadingMoreHistory(false);
    }
  };

  const getTransactionLabel = (tx: Transaction): string => {
    const formatPlanName = (plan: string | undefined): string | null => {
      if (!plan) return null;
      // Handle plan names like "family_sub" -> "Family"
      return plan.replace('_sub', '').charAt(0).toUpperCase() + plan.replace('_sub', '').slice(1);
    };

    if (tx.type === 'credit_purchase') {
      const packageName = tx.package ? tx.package.charAt(0).toUpperCase() + tx.package.slice(1) : 'Credit';
      return `${packageName} Pack (${tx.credits_added || 0} credits)`;
    } else if (tx.type === 'subscription') {
      return `${formatPlanName(tx.plan) || ''} Subscription`;
    } else if (tx.type === 'subscription_renewal') {
      return `${formatPlanName(tx.plan) || ''} Renewal`;
    } else if (tx.type === 'plan_change') {
      const oldPlan = formatPlanName(tx.old_plan);
      const newPlan = formatPlanName(tx.plan);
      if (oldPlan && newPlan) {
        return `Plan Change: ${oldPlan} → ${newPlan}`;
      } else if (newPlan) {
        return `Switched to ${newPlan} Plan`;
      }
      return 'Plan Change';
    }
    return tx.type || 'Transaction';
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const updateData: Partial<UserProfile> = {};
      if (editName !== user?.name) updateData.name = editName;
      if (editHandle !== (user?.handle || '')) updateData.handle = editHandle;
      if (editBio !== (user?.bio || '')) updateData.bio = editBio;
      if (editPicture !== (user?.picture || '')) updateData.picture = editPicture;

      if (Object.keys(updateData).length === 0) {
        setIsEditing(false);
        return;
      }

      const response = await axios.put<UserProfile>(`${API}/profile`, updateData, {
        withCredentials: true
      });
      setUser(response.data);
      setIsEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.detail || 'Failed to update profile');
      } else {
        toast.error('Failed to update profile');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (): void => {
    setEditName(user?.name || '');
    setEditHandle(user?.handle || '');
    setEditBio(user?.bio || '');
    setEditPicture(user?.picture || '');
    setIsEditing(false);
  };

  const handleAvatarUpload = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPEG, PNG, GIF, or WebP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post<{ url: string }>(`${API}/profile/avatar`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const avatarUrl = response.data.url;
      setEditPicture(getImageUrl(avatarUrl) || '');
      setUser(prev => prev ? { ...prev, picture: avatarUrl } : null);
      toast.success('Avatar uploaded!');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.detail || 'Failed to upload avatar');
      } else {
        toast.error('Failed to upload avatar');
      }
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRedeemCode = async (): Promise<void> => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    setRedeemLoading(true);
    try {
      const response = await axios.post<{ message: string; total_credits: number }>(`${API}/auth/redeem-code`, {
        code: promoCode
      }, {
        withCredentials: true
      });

      toast.success(response.data.message);
      setPromoCode('');
      // Update user credits
      setUser(prev => prev ? { ...prev, credits: response.data.total_credits } : null);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.detail || 'Failed to redeem code');
      } else {
        toast.error('Failed to redeem code');
      }
    } finally {
      setRedeemLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleDeleteAccount = async (): Promise<void> => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/auth/account`, { withCredentials: true });

      // Clear local storage and redirect
      localStorage.clear();
      toast.success('Account deleted successfully');
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.detail || 'Failed to delete account');
      } else {
        toast.error('Failed to delete account');
      }
    } finally {
      setDeletingAccount(false);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex-1 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: 'var(--st-primary)' }} />
            <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading profile...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto px-4 py-4 md:py-6 md:px-8 2xl:px-12">
          {/* Page Header */}
          <div className="rounded-2xl p-4 md:p-5 mb-5 shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  My Profile
                </h1>
                <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  Manage your account and settings
                </p>
              </div>
            </div>
          </div>

          {/* Profile Card */}
          <div className="rounded-2xl p-4 md:p-5 mb-5 shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
            {isEditing ? (
              /* Mobile-optimized Edit Mode - Full width centered layout */
              <div className="space-y-5">
                {/* Centered Profile Picture */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    {editPicture ? (
                      <img src={getImageUrl(editPicture) || undefined} alt="Profile" className="w-28 h-28 md:w-24 md:h-24 rounded-2xl object-cover shadow-md" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-28 h-28 md:w-24 md:h-24 rounded-2xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}>
                        <User className="w-12 h-12 md:w-10 md:h-10" style={{ color: 'var(--st-primary)' }} />
                      </div>
                    )}
                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingAvatar}
                      className="absolute -bottom-2 -right-2 p-2.5 md:p-2 rounded-full shadow-lg disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)' }}
                    >
                      {uploadingAvatar ? (
                        <Loader2 className="w-5 h-5 md:w-4 md:h-4 text-white animate-spin" />
                      ) : (
                        <Camera className="w-5 h-5 md:w-4 md:h-4 text-white" />
                      )}
                    </button>
                    {/* Clear photo button */}
                    {editPicture && (
                      <button
                        onClick={() => setEditPicture('')}
                        className="absolute -top-2 -right-2 p-2 md:p-1.5 rounded-full shadow-lg bg-red-500 hover:bg-red-600"
                        title="Remove photo"
                      >
                        <X className="w-4 h-4 md:w-3 md:h-3 text-white" />
                      </button>
                    )}
                  </div>
                  <p className="text-xs mt-3" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    Tap to change photo
                  </p>
                </div>

                {/* Full-width Form Fields */}
                <div className="space-y-4">
                  {/* Display Name Field */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold mb-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                      <User className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Your name"
                      className="w-full px-4 py-3.5 md:py-3 rounded-xl text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 dark:focus:ring-orange-800 transition-all"
                      style={{ fontFamily: 'Quicksand, sans-serif', border: '2px solid var(--st-border-light)', background: 'var(--st-bg-surface)', color: 'var(--st-text-heading)' }}
                    />
                  </div>

                  {/* Handle Field */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold mb-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                      <span className="w-4 h-4 flex items-center justify-center text-sm font-bold" style={{ color: 'var(--st-primary)' }}>@</span>
                      Handle
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base md:text-sm font-semibold" style={{ color: 'var(--st-primary)' }}>@</span>
                      <input
                        type="text"
                        value={editHandle}
                        onChange={(e) => setEditHandle(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase())}
                        placeholder="username"
                        className="w-full pl-9 pr-4 py-3.5 md:py-3 rounded-xl text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 dark:focus:ring-orange-800 transition-all"
                        style={{ fontFamily: 'Quicksand, sans-serif', border: '2px solid var(--st-border-light)', background: 'var(--st-bg-surface)', color: 'var(--st-text-heading)' }}
                      />
                    </div>
                    <p className="text-xs mt-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                      Letters, numbers, and underscores only
                    </p>
                  </div>

                  {/* Bio Field */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold mb-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                      <BookOpen className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
                      Bio
                    </label>
                    <textarea
                      value={editBio}
                      onChange={(e) => setEditBio(e.target.value)}
                      placeholder="Tell us about yourself..."
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3.5 md:py-3 rounded-xl text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 dark:focus:ring-orange-800 resize-none transition-all"
                      style={{ fontFamily: 'Quicksand, sans-serif', border: '2px solid var(--st-border-light)', background: 'var(--st-bg-surface)', color: 'var(--st-text-heading)' }}
                    />
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        Share a bit about yourself
                      </p>
                      <p className="text-xs font-semibold" style={{ color: editBio.length > 450 ? 'var(--st-primary)' : 'var(--st-text-muted)' }}>
                        {editBio.length}/500
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons - Larger touch targets on mobile */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleCancel}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-4 md:py-3 rounded-xl font-semibold text-base md:text-sm transition-all active:scale-[0.98]"
                    style={{ fontFamily: 'Quicksand, sans-serif', border: '2px solid var(--st-border-light)', color: 'var(--st-text-muted)', background: 'var(--st-bg-surface)' }}
                  >
                    <X className="w-5 h-5 md:w-4 md:h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-4 md:py-3 rounded-xl font-semibold text-base md:text-sm transition-all disabled:opacity-50 active:scale-[0.98]"
                    style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)', color: 'white', fontFamily: 'Quicksand, sans-serif' }}
                  >
                    {saving ? (
                      <Loader2 className="w-5 h-5 md:w-4 md:h-4 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-5 h-5 md:w-4 md:h-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
            /* View Mode - Original horizontal layout */
            <div className="flex flex-row items-start gap-4">
              {/* Profile Picture */}
              <div className="relative flex-shrink-0">
                {user?.picture ? (
                  <img src={getImageUrl(user.picture) || undefined} alt={user.name} className="w-20 h-20 rounded-xl object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-20 h-20 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}>
                    <User className="w-8 h-8" style={{ color: 'var(--st-primary)' }} />
                  </div>
                )}
              </div>

              {/* Profile Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  {user?.name}
                </h1>
                {user?.handle && (
                  <p className="text-xs mb-1" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    @{user.handle}
                  </p>
                )}
                {user?.bio && (
                  <p className="text-sm mb-2 max-w-md" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                    {user.bio}
                  </p>
                )}
                <div className="flex items-center gap-3 text-xs flex-wrap" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  <span className="flex items-center gap-1">
                    <BookOpen className="w-3.5 h-3.5" />
                    {user?.story_count || 0} stories
                  </span>
                  <span className="flex items-center gap-1">
                    <Heart className="w-3.5 h-3.5" />
                    {user?.favorites?.length || 0} favorites
                  </span>
                  <button
                    onClick={() => navigate('/profile/connections?tab=followers')}
                    className="flex items-center gap-1 hover:underline transition-all"
                  >
                    <Users className="w-3.5 h-3.5" />
                    {user?.followers_count || 0} followers
                  </button>
                  <button
                    onClick={() => navigate('/profile/connections?tab=following')}
                    className="flex items-center gap-1 hover:underline transition-all"
                  >
                    <Users className="w-3.5 h-3.5" />
                    {user?.following_count || 0} following
                  </button>
                </div>
              </div>

              {/* Edit Button */}
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-black/5 dark:hover:bg-white/10 transition-all shadow-sm flex-shrink-0"
                style={{ fontFamily: 'Quicksand, sans-serif', border: '1px solid var(--st-border-light)', color: 'var(--st-text-heading)' }}
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
            </div>
            )}

            {/* Stats Bar and other sections - hidden during edit */}
            {!isEditing && (
            <>
            {/* Stats Row - Clean 4-stat layout */}
            <div className="mt-4 pt-4 border-t grid grid-cols-2 sm:grid-cols-4 gap-2" style={{ borderColor: 'var(--st-border-light)' }}>
              <div
                className="text-center p-3 rounded-xl cursor-pointer hover:scale-[1.02] transition-all bg-orange-50/50 dark:bg-orange-900/20"
                style={{ border: '1px solid var(--st-border-light)' }}
                onClick={() => navigate('/pricing')}
              >
                <p className="text-2xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-primary)' }}>
                  {user?.credits || 0}
                </p>
                <p className="text-xs font-medium" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Credits</p>
              </div>
              <div
                className="text-center p-3 rounded-xl cursor-pointer hover:scale-[1.02] transition-all bg-gray-50 dark:bg-white/5"
                style={{ border: '1px solid var(--st-border-light)' }}
                onClick={() => navigate('/library')}
              >
                <p className="text-2xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  {user?.story_count || 0}
                </p>
                <p className="text-xs font-medium" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Stories</p>
              </div>
              <div
                className="text-center p-3 rounded-xl cursor-pointer hover:scale-[1.02] transition-all bg-indigo-50 dark:bg-indigo-900/20"
                style={{ border: '1px solid var(--st-border-light)' }}
                onClick={() => navigate('/series')}
              >
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400" style={{ fontFamily: 'Chewy, cursive' }}>
                  {user?.series_count || 0}
                </p>
                <p className="text-xs font-medium text-indigo-500 dark:text-indigo-400" style={{ fontFamily: 'Quicksand, sans-serif' }}>Series</p>
              </div>
              <div
                className={`text-center p-3 rounded-xl cursor-pointer hover:scale-[1.02] transition-all ${
                  user?.is_premium
                    ? 'bg-amber-50 dark:bg-amber-900/20'
                    : 'bg-gray-50 dark:bg-white/5'
                }`}
                style={{ border: user?.is_premium ? '1px solid var(--st-border-light)' : '1px solid var(--st-border-light)' }}
                onClick={() => navigate('/pricing')}
              >
                <p className={`text-2xl font-bold flex items-center justify-center gap-1 ${user?.is_premium ? 'text-amber-600 dark:text-amber-400' : ''}`} style={{ fontFamily: 'Chewy, cursive', color: user?.is_premium ? undefined : 'var(--st-text-muted)' }}>
                  {user?.is_premium && <Crown className="w-5 h-5" />}
                  {user?.is_premium ? '' : 'Free'}
                </p>
                <p className={`text-xs font-medium ${user?.is_premium ? 'text-amber-600 dark:text-amber-400' : ''}`} style={{ fontFamily: 'Quicksand, sans-serif', color: user?.is_premium ? undefined : 'var(--st-text-muted)' }}>
                  {user?.is_premium ? 'Premium' : 'Plan'}
                </p>
              </div>
            </div>

            {/* Admin Panel Link - Only for admins */}
            {user?.is_admin && (
              <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--st-border-light)' }}>
                <button
                  onClick={() => navigate('/admin')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90"
                  style={{
                    background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                    color: 'white',
                    fontFamily: 'Quicksand, sans-serif'
                  }}
                >
                  <Shield className="w-4 h-4" />
                  Admin Panel
                </button>
              </div>
            )}

            {/* Redeem Code Section */}
            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--st-border-light)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                <h3 className="font-bold text-xs" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  Redeem Promo Code
                </h3>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter promo code"
                  className="flex-1 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
                  style={{ fontFamily: 'Quicksand, sans-serif', border: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)', color: 'var(--st-text-heading)' }}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleRedeemCode()}
                />
                <button
                  onClick={handleRedeemCode}
                  disabled={redeemLoading || !promoCode.trim()}
                  className="px-4 py-2 rounded-lg font-semibold text-sm transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)', color: 'white', fontFamily: 'Quicksand, sans-serif' }}
                >
                  {redeemLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Redeem'
                  )}
                </button>
              </div>
            </div>

            {/* Subscription & Credits Section - Clean minimal design */}
            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--st-border-light)' }}>
              {user?.is_premium ? (
                /* Premium User - Clean card design */
                <div className="space-y-3">
                  {/* Cancellation Warning Banner */}
                  {subscription?.cancel_at_period_end && (
                    <div
                      className="p-4 rounded-xl flex items-start gap-3"
                      style={{ background: '#FEF3C7', border: '1px solid #FCD34D' }}
                    >
                      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} />
                      <div className="flex-1">
                        <p className="font-semibold text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: '#92400E' }}>
                          Your subscription is scheduled to cancel
                        </p>
                        <p className="text-xs mt-1" style={{ fontFamily: 'Quicksand, sans-serif', color: '#A16207' }}>
                          You'll lose access to premium features on{' '}
                          <strong>{subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'your billing date'}</strong>.
                          Your unused credits will remain in your account.
                        </p>
                        <button
                          onClick={handleReactivateSubscription}
                          disabled={reactivatingSubscription}
                          className="mt-3 px-4 py-2 rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                          style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)', color: 'white', fontFamily: 'Quicksand, sans-serif' }}
                        >
                          {reactivatingSubscription ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="w-4 h-4" />
                              Keep My Subscription
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Plan Card */}
                  <div className="rounded-xl overflow-hidden" style={{ border: subscription?.cancel_at_period_end ? '1px solid #FCD34D' : '1px solid var(--st-border-light)' }}>
                    {/* Plan Header Row */}
                    <div className="flex items-center justify-between p-4 bg-orange-50/50 dark:bg-white/5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: subscription?.cancel_at_period_end ? '#FEF3C7' : 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)' }}>
                          <Crown className="w-5 h-5" style={{ color: subscription?.cancel_at_period_end ? '#D97706' : 'white' }} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                              {subscription?.plan_name || (user?.subscription_plan ? user.subscription_plan.charAt(0).toUpperCase() + user.subscription_plan.slice(1).replace('_sub', '') : 'Premium')} Plan
                            </span>
                            {subscription?.cancel_at_period_end ? (
                              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#FEF3C7', color: '#D97706' }}>Ending Soon</span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#DCFCE7', color: '#16A34A' }}>Active</span>
                            )}
                          </div>
                          <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                            {subscription?.current_period_end
                              ? `${subscription.cancel_at_period_end ? 'Ends' : 'Renews'} ${new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                              : `${subscription?.credits_per_month || 0} credits/month`
                            }
                          </p>
                        </div>
                      </div>
                      <span className="font-bold" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-primary)' }}>
                        {PLAN_BENEFITS[subscription?.plan || user?.subscription_plan || '']?.price || ''}
                      </span>
                    </div>

                    {/* Action Buttons - Simple row */}
                    <div className="flex border-t" style={{ borderColor: 'var(--st-border-light)' }}>
                      <button
                        onClick={() => navigate('/pricing')}
                        className="flex-1 py-3 text-sm font-semibold transition-all hover:bg-orange-50 dark:hover:bg-white/10 flex items-center justify-center gap-1.5 border-r"
                        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-primary)', borderColor: 'var(--st-border-light)' }}
                      >
                        <Coins className="w-4 h-4" />
                        Buy Credits
                      </button>
                      <button
                        onClick={() => navigate('/pricing?tab=subscription')}
                        className="flex-1 py-3 text-sm font-semibold transition-all hover:bg-orange-50 dark:hover:bg-white/10 flex items-center justify-center gap-1.5 border-r"
                        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)', borderColor: 'var(--st-border-light)' }}
                      >
                        Manage Plan
                      </button>
                      {!subscription?.cancel_at_period_end && (
                        <button
                          onClick={handleCancelSubscription}
                          disabled={cancelingSubscription}
                          className="px-4 py-3 text-sm font-medium transition-all hover:bg-red-50 disabled:opacity-50"
                          style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                        >
                          {cancelingSubscription ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* Free User - Simple upgrade row */
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--st-border-light)' }}>
                  <div className="flex items-center justify-between p-4 bg-orange-50/50 dark:bg-white/5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--st-border-light)' }}>
                        <Crown className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                      </div>
                      <div>
                        <span className="font-bold text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                          Upgrade to Premium
                        </span>
                        <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                          Audio, pro layouts & monthly credits
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/pricing?tab=subscription')}
                      className="px-4 py-2 rounded-lg font-semibold text-sm transition-all"
                      style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)', color: 'white', fontFamily: 'Quicksand, sans-serif' }}
                    >
                      View Plans
                    </button>
                  </div>
                </div>
              )}

              {/* Billing History - Collapsible */}
              <div className="mt-3 rounded-xl overflow-hidden" style={{ border: '1px solid var(--st-border-light)' }}>
                <button
                  onClick={() => {
                    setShowBillingHistory(!showBillingHistory);
                    if (!showBillingHistory && billingHistory.length === 0) {
                      loadBillingHistory();
                    }
                  }}
                  className="w-full flex items-center justify-between p-3 transition-all hover:bg-orange-50/50 dark:hover:bg-white/10"
                >
                  <div className="flex items-center gap-2">
                    <Receipt className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
                    <span className="text-sm font-medium" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                      Billing History
                    </span>
                  </div>
                  <ChevronRight
                    className="w-4 h-4 transition-transform"
                    style={{ color: 'var(--st-text-muted)', transform: showBillingHistory ? 'rotate(90deg)' : 'rotate(0deg)' }}
                  />
                </button>

                {showBillingHistory && (
                  <div className="border-t" style={{ borderColor: 'var(--st-border-light)' }}>
                    {loadingBillingHistory ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--st-primary)' }} />
                      </div>
                    ) : billingHistory.length === 0 ? (
                      <div className="py-6 text-center">
                        <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                          No transactions yet
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className="divide-y" style={{ borderColor: 'var(--st-border-light)' }}>
                          {billingHistory.map((tx, index) => (
                            <div key={tx.transaction_id || index} className="flex items-center justify-between px-4 py-3 hover:bg-orange-50/30 dark:hover:bg-white/5">
                              <div>
                                <p className="text-sm font-medium" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                                  {getTransactionLabel(tx)}
                                </p>
                                <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                                  {formatDate(tx.created_at)}
                                </p>
                              </div>
                              <span className="text-sm font-bold" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-primary)' }}>
                                {tx.amount_formatted}
                              </span>
                            </div>
                          ))}
                        </div>
                        {/* Load More Button */}
                        {billingHistory.length < billingHistoryTotal && (
                          <div className="p-3 border-t" style={{ borderColor: 'var(--st-border-light)' }}>
                            <button
                              onClick={() => loadBillingHistory(true)}
                              disabled={loadingMoreHistory}
                              className="w-full py-2 text-sm font-medium rounded-lg transition-all hover:bg-orange-50 dark:hover:bg-white/10 disabled:opacity-50 flex items-center justify-center gap-2"
                              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-primary)', border: '1px solid var(--st-border-light)' }}
                            >
                              {loadingMoreHistory ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Loading...
                                </>
                              ) : (
                                <>
                                  Load More ({billingHistoryTotal - billingHistory.length} remaining)
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Danger Zone - Collapsible */}
              <div className="mt-3 rounded-xl overflow-hidden" style={{ border: '1px solid var(--st-border-light)' }}>
                <button
                  onClick={() => setShowDangerZone(!showDangerZone)}
                  className="w-full flex items-center justify-between p-3 transition-all hover:bg-orange-50/50 dark:hover:bg-white/10"
                >
                  <div className="flex items-center gap-2">
                    <Trash2 className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                    <span className="text-sm font-medium" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                      Delete Account
                    </span>
                  </div>
                  <ChevronRight
                    className="w-4 h-4 transition-transform"
                    style={{ color: 'var(--st-text-muted)', transform: showDangerZone ? 'rotate(90deg)' : 'rotate(0deg)' }}
                  />
                </button>

                {showDangerZone && (
                  <div className="border-t p-4" style={{ borderColor: 'var(--st-border-light)' }}>
                    <p className="text-sm mb-3" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                      Permanently delete your account and all associated data including:
                    </p>

                    <ul className="space-y-1.5 mb-3">
                      {['All your stories', 'All your series', 'Your profile and settings'].map((item) => (
                        <li key={item} className="flex items-center gap-2 text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                          <span className="w-1 h-1 rounded-full" style={{ background: 'var(--st-text-muted)' }} />
                          {item}
                        </li>
                      ))}
                    </ul>

                    <p className="text-xs mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                      This action cannot be undone.
                    </p>

                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="px-4 py-2 rounded-lg font-semibold text-sm transition-all hover:bg-red-700"
                      style={{
                        background: '#DC2626',
                        color: 'white',
                        fontFamily: 'Quicksand, sans-serif'
                      }}
                    >
                      Delete My Account
                    </button>
                  </div>
                )}
              </div>
            </div>
            </>
            )}
          </div>

          {/* User's Stories */}
          <div id="my-stories-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                My Stories
              </h2>
              <span className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                {totalStories} {totalStories === 1 ? 'story' : 'stories'}
              </span>
            </div>

            {loadingStories && stories.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--st-primary)' }} />
              </div>
            ) : stories.length === 0 ? (
              <div className="rounded-2xl p-12 text-center shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                <Sparkles className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--st-border-medium)' }} />
                <p className="font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>No stories yet</p>
                <p className="text-sm mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  Create your first magical story!
                </p>
                <button
                  onClick={() => navigate('/create')}
                  className="px-5 py-2.5 rounded-lg font-semibold text-sm"
                  style={{ background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)', color: 'white', fontFamily: 'Quicksand, sans-serif' }}
                >
                  Create Story
                </button>
              </div>
            ) : (
              <>
                {/* Stories List */}
                <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                  {loadingStories && (
                    <div className="absolute inset-0 bg-white/50 dark:bg-black/30 flex items-center justify-center z-10 rounded-2xl">
                      <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--st-primary)' }} />
                    </div>
                  )}
                  {stories.map((story, index) => (
                    <div
                      key={story.id}
                      className="flex items-center gap-4 p-3 hover:bg-black/5 dark:hover:bg-white/10 hover:scale-[1.01] transition-all duration-200 cursor-pointer group"
                      style={{ borderBottom: index < stories.length - 1 ? '1px solid var(--st-border-light)' : undefined }}
                      onClick={() => navigate(`/story/${story.id}`)}
                    >
                      {/* Thumbnail with page count overlay */}
                      <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0">
                        {story.thumbnail ? (
                          <img src={getImageUrl(story.thumbnail) || undefined} alt={story.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}>
                            <Sparkles className="w-6 h-6" style={{ color: 'var(--st-primary-hover)' }} />
                          </div>
                        )}
                        {story.pages && story.pages.length > 0 && (
                          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-xs font-bold text-white" style={{ background: 'rgba(0,0,0,0.6)', fontSize: '10px' }}>
                            {story.pages.length}p
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-bold text-sm md:text-base truncate" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                            {story.title}
                          </h3>
                          {story.age_range && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0" style={{ background: 'var(--st-border-light)', color: 'var(--st-text-heading)' }}>
                              {getBookTypeLabel(story.age_range)}
                            </span>
                          )}
                          <VersionBadge version={story.version} isProStory={story.is_pro_story} size="small" />
                          {story.is_public === false && (
                            <Lock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--st-text-muted)' }} />
                          )}
                        </div>
                        {story.prompt && (
                          <p className="text-xs md:text-sm truncate" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                            {story.prompt}
                          </p>
                        )}
                        <p className="text-xs mt-1" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                          {formatDate(story.created_at)}
                        </p>
                      </div>

                      {/* Stats */}
                      <div className="hidden md:flex items-center gap-4 text-xs flex-shrink-0" style={{ color: 'var(--st-text-muted)' }}>
                        {story.views_count !== undefined && story.views_count > 0 && (
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {story.views_count}
                          </span>
                        )}
                        {story.favorites_count !== undefined && story.favorites_count > 0 && (
                          <span className="flex items-center gap-1">
                            <Heart className="w-4 h-4" />
                            {story.favorites_count}
                          </span>
                        )}
                      </div>

                      {/* Arrow indicator */}
                      <ChevronRight className="w-5 h-5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--st-primary)' }} />
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    {/* Previous button */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loadingStories}
                      className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5 dark:hover:bg-white/10"
                      style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)', border: '1px solid var(--st-border-light)' }}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline">Previous</span>
                    </button>

                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {(() => {
                        const pages: (number | string)[] = [];
                        const maxVisiblePages = 5;

                        if (totalPages <= maxVisiblePages) {
                          // Show all pages if total is small
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          // Show first page
                          pages.push(1);

                          if (currentPage > 3) {
                            pages.push('...');
                          }

                          // Show pages around current
                          const start = Math.max(2, currentPage - 1);
                          const end = Math.min(totalPages - 1, currentPage + 1);

                          for (let i = start; i <= end; i++) {
                            if (!pages.includes(i)) {
                              pages.push(i);
                            }
                          }

                          if (currentPage < totalPages - 2) {
                            pages.push('...');
                          }

                          // Show last page
                          if (!pages.includes(totalPages)) {
                            pages.push(totalPages);
                          }
                        }

                        return pages.map((page, idx) => (
                          page === '...' ? (
                            <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm" style={{ color: 'var(--st-text-muted)' }}>...</span>
                          ) : (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page as number)}
                              disabled={loadingStories}
                              className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all ${
                                currentPage === page
                                  ? ''
                                  : 'hover:bg-black/5 dark:hover:bg-white/10'
                              }`}
                              style={{
                                fontFamily: 'Quicksand, sans-serif',
                                background: currentPage === page ? 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)' : undefined,
                                color: currentPage === page ? 'white' : 'var(--st-text-heading)',
                                border: currentPage === page ? undefined : '1px solid var(--st-border-light)'
                              }}
                            >
                              {page}
                            </button>
                          )
                        ));
                      })()}
                    </div>

                    {/* Next button */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || loadingStories}
                      className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5 dark:hover:bg-white/10"
                      style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)', border: '1px solid var(--st-border-light)' }}
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Page info */}
                <div className="text-center mt-3">
                  <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    Showing {((currentPage - 1) * STORIES_PER_PAGE) + 1}-{Math.min(currentPage * STORIES_PER_PAGE, totalStories)} of {totalStories} stories
                  </p>
                </div>
              </>
            )}
          </div>

        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowCancelModal(false)}
          />

          {/* Modal */}
          <div
            className="relative rounded-2xl shadow-xl max-w-sm w-full overflow-hidden"
            style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
          >
            {/* Header */}
            <div className="p-5 pb-0">
              <div className="flex items-start justify-between">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: '#FEE2E2' }}
                >
                  <Crown className="w-6 h-6" style={{ color: '#DC2626' }} />
                </div>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" style={{ color: 'var(--st-text-muted)' }} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              <h3
                className="text-lg font-bold mb-2"
                style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
              >
                Cancel Subscription?
              </h3>
              <p
                className="text-sm mb-4"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
              >
                Are you sure you want to cancel? You'll keep access to premium features until the end of your billing period.
              </p>

              {/* What you'll lose */}
              <div
                className="p-4 rounded-xl mb-4"
                style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}
              >
                <p className="text-xs font-semibold mb-2" style={{ fontFamily: 'Quicksand, sans-serif', color: '#DC2626' }}>
                  You'll lose access to:
                </p>
                <ul className="space-y-1.5">
                  {['Monthly credits', 'Audio narration', 'Pro picture book layouts', 'Photo transform'].map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: '#7F1D1D' }}>
                      <X className="w-3 h-3" style={{ color: '#DC2626' }} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-5 pt-0">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all hover:opacity-90"
                style={{
                  fontFamily: 'Quicksand, sans-serif',
                  background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                  color: 'white'
                }}
              >
                Keep Subscription
              </button>
              <button
                onClick={confirmCancelSubscription}
                className="flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all hover:bg-red-100"
                style={{
                  fontFamily: 'Quicksand, sans-serif',
                  background: '#FEE2E2',
                  color: '#DC2626'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText('');
            }}
          />

          {/* Modal */}
          <div
            className="relative rounded-2xl shadow-xl max-w-sm w-full overflow-hidden"
            style={{ background: 'var(--st-bg-surface)', border: '1px solid #FCA5A5' }}
          >
            {/* Header */}
            <div className="p-5 pb-0">
              <div className="flex items-start justify-between">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: '#FEE2E2' }}
                >
                  <Trash2 className="w-6 h-6" style={{ color: '#DC2626' }} />
                </div>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                  }}
                  className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" style={{ color: 'var(--st-text-muted)' }} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              <h3
                className="text-lg font-bold mb-2"
                style={{ fontFamily: 'Chewy, cursive', color: '#DC2626' }}
              >
                Delete Account?
              </h3>
              <p
                className="text-sm mb-4"
                style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
              >
                This will permanently delete your account, all your stories, series, and data. This action cannot be undone.
              </p>

              {/* Confirmation Input */}
              <div className="mb-4">
                <label
                  className="block text-xs font-semibold mb-2"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                >
                  Type <span style={{ color: '#DC2626' }}>DELETE</span> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                  placeholder="DELETE"
                  className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 transition-all"
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    border: '2px solid #FCA5A5',
                    background: 'var(--st-bg-surface)',
                    color: 'var(--st-text-heading)'
                  }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-5 pt-0">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                className="flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all hover:bg-black/5 dark:hover:bg-white/10"
                style={{
                  fontFamily: 'Quicksand, sans-serif',
                  border: '1px solid var(--st-border-light)',
                  color: 'var(--st-text-heading)',
                  background: 'var(--st-bg-surface)'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                className="flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all hover:bg-red-700 disabled:opacity-50 disabled:hover:bg-red-600 flex items-center justify-center gap-2"
                style={{
                  fontFamily: 'Quicksand, sans-serif',
                  background: '#DC2626',
                  color: 'white'
                }}
              >
                {deletingAccount ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Delete Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ProfilePage;
